(function () {
  'use strict';

  const SELECTORS = {
    megaMenu: '[data-mega-menu]',
    l2Item: '.mega-menu__item--l2',
    l3GroupItem: '.mega-menu__item--l3-group',
    l3CmsItem: '.mega-menu__item--l3-cms',
    l2GroupsPanel: '.mega-menu__l2-groups-panel',
    l3CmsPanel: '[data-l3-cms-panel]',
    mediaPanel: '[data-media-panel]',
    navItem: '[data-nav-item]',
    disclosure: '[data-hover-disclosure]',
    header: '[data-site-header]',
    headerBackground: '[data-header-background]',
  };

  const ACTIVE_CLASS = 'is-active';
  const VISIBLE_CLASS = 'is-visible';
  const HEADER_MENU_OPENED_CLASS = 'site-header--menu-opened';

  const instances = new WeakMap();
  let syncDropdownHeightFrame = 0;

  function normalizeLabel(label) {
    if (!label) return '';
    return String(label)
      .replace(/<[^>]*>/g, '')
      .trim()
      .toLowerCase();
  }

  function isDesignMode() {
    return Boolean(window.Shopify?.designMode);
  }

  function resetHeaderBackground() {
    const background = document.querySelector(SELECTORS.headerBackground);
    if (!background) return;

    background.style.removeProperty('height');
    background.style.removeProperty('--header-background-height');
  }

  function resetDisclosureHeights() {
    document.querySelectorAll(SELECTORS.disclosure).forEach((disclosure) => {
      disclosure.style.removeProperty('height');
    });
  }

  function syncDropdownHeight(megaMenu) {
    const navItem = megaMenu.closest(SELECTORS.navItem);
    if (!navItem?.classList.contains(VISIBLE_CLASS)) {
      return;
    }

    const disclosure = navItem.querySelector(SELECTORS.disclosure);
    const header = document.querySelector(SELECTORS.header);
    const background = document.querySelector(SELECTORS.headerBackground);
    if (!disclosure || !header || !background) return;

    if (syncDropdownHeightFrame) {
      cancelAnimationFrame(syncDropdownHeightFrame);
    }

    syncDropdownHeightFrame = requestAnimationFrame(() => {
      syncDropdownHeightFrame = requestAnimationFrame(() => {
        if (!navItem.classList.contains(VISIBLE_CLASS)) {
          return;
        }

        disclosure.style.height = 'auto';

        const hasScrolled = document.body.classList.contains('has-scrolled');
        const headerHeight = hasScrolled
          ? window.stickyHeaderHeight || header.offsetHeight
          : header.offsetHeight;
        const nextHeight = disclosure.offsetHeight + headerHeight;

        background.style.removeProperty('height');
        background.style.setProperty('--header-background-height', `${nextHeight}px`);
      });
    });
  }

  function bindHeaderBackgroundReset(header) {
    if (!header || header.dataset.megaMenuBackgroundReset) return;

    header.dataset.megaMenuBackgroundReset = 'true';

    const observer = new MutationObserver(() => {
      if (header.classList.contains(HEADER_MENU_OPENED_CLASS)) return;

      if (syncDropdownHeightFrame) {
        cancelAnimationFrame(syncDropdownHeightFrame);
        syncDropdownHeightFrame = 0;
      }

      resetHeaderBackground();
      resetDisclosureHeights();
    });

    observer.observe(header, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  class MegaMenu {
    constructor(root) {
      this.root = root;
      this.hasL3Cms = root.classList.contains('mega-menu--has-l3-cms');
      this.l2Items = Array.from(root.querySelectorAll(SELECTORS.l2Item));
      this.l2GroupsPanels = Array.from(root.querySelectorAll(SELECTORS.l2GroupsPanel));
      this.l3CmsPanels = Array.from(root.querySelectorAll(SELECTORS.l3CmsPanel));
      this.mediaPanels = Array.from(root.querySelectorAll(SELECTORS.mediaPanel));
      this.activeMediaPanel = null;

      this.activeL2 = this.l2Items.find((item) => item.classList.contains(ACTIVE_CLASS)) || this.l2Items[0];

      const initialGroupsPanel =
        this.l2GroupsPanels.find((panel) => panel.classList.contains(ACTIVE_CLASS)) || this.l2GroupsPanels[0];

      this.activeL3Group =
        initialGroupsPanel?.querySelector(`${SELECTORS.l3GroupItem}.${ACTIVE_CLASS}`) ||
        initialGroupsPanel?.querySelector(SELECTORS.l3GroupItem) ||
        null;

      this.bindEvents();
      this.setActiveL3Group(this.activeL3Group, initialGroupsPanel);
      this.syncState(false);
    }

    bindEvents() {
      const l2Col = this.root.querySelector('.mega-menu__col--l2');

      this.l2Items.forEach((item) => {
        item.addEventListener('mouseenter', () => this.scheduleActivateL2(item, l2Col));
        item.addEventListener('mouseleave', () => this.cancelScheduledActivateL2(item));
        item.addEventListener('focusin', () => {
          this.cancelScheduledActivateL2();
          this.activateL2(item);
        });
      });

      this.root.querySelectorAll(SELECTORS.l3GroupItem).forEach((item) => {
        item.addEventListener('mouseenter', () => {
          this.cancelScheduledActivateL2();
          this.activateL3Group(item);
        });
        item.addEventListener('focusin', () => this.activateL3Group(item));
      });

      this.root.querySelectorAll(SELECTORS.l3CmsItem).forEach((item) => {
        item.addEventListener('mouseenter', () => {
          this.cancelScheduledActivateL2();
          this.activateL3Cms(item);
        });
        item.addEventListener('focusin', () => this.activateL3Cms(item));
      });

      this.root
        .querySelectorAll('.mega-menu__col--l2-groups, .mega-menu__col--l3-cms, .mega-menu__col--media')
        .forEach((col) => {
          col.addEventListener('mouseenter', () => this.cancelScheduledActivateL2());
        });

      // Keyboard Arrow Navigation for ADA
      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          this.handleArrowKey(e);
        } else if (e.key === 'Escape' || e.key === 'Esc') {
          this.handleEscapeKey(e);
        }
      });
    }

    handleEscapeKey(e) {
      const navItem = this.root.closest('.menu__item.mega-menu-enabled');
      if (!navItem) return;

      e.preventDefault();
      e.stopPropagation();

      // Close the mega menu immediately
      navItem.classList.remove('is-visible');
      navItem.setAttribute('aria-expanded', 'false');

      const siteHeader = navItem.closest('[data-site-header]');
      if (siteHeader && !siteHeader.querySelector('.menu__item.mega-menu-enabled.is-visible')) {
        siteHeader.classList.remove('site-header--menu-opened');
        document.body.classList.remove('megamenu-opened');
        
        const bg = document.querySelector('[data-header-background]');
        if (bg) {
          bg.style.removeProperty('height');
          bg.style.removeProperty('--header-background-height');
        }
        document.querySelectorAll('[data-hover-disclosure]').forEach(d => d.style.removeProperty('height'));
      }

      // Blur current active element to prevent interference
      if (document.activeElement) document.activeElement.blur();

      // Find the NEXT top-level navigation item and focus it
      const allTopLinks = Array.from(document.querySelectorAll('.navlink--toplevel')).filter(
        link => link.offsetParent !== null
      );
      
      const currentTopLink = navItem.querySelector('.navlink--toplevel');
      const currentIndex = allTopLinks.indexOf(currentTopLink);
      
      let targetLink = null;
      if (currentIndex !== -1 && currentIndex < allTopLinks.length - 1) {
        targetLink = allTopLinks[currentIndex + 1];
      } else {
        targetLink = currentTopLink;
      }
      
      if (targetLink) {
        targetLink.focus();
      }
    }

    handleArrowKey(e) {
      const activeEl = document.activeElement;
      if (!activeEl) return;

      const isL2Link = activeEl.classList.contains('mega-menu__link--l2');
      const isL3GroupLink = activeEl.classList.contains('mega-menu__link--l3-group');
      
      // Moving deeper (L2 -> L3)
      if (e.key === 'ArrowRight' && isL2Link) {
        const l2Item = activeEl.closest('.mega-menu__item--l2');
        if (l2Item && l2Item.getAttribute('data-has-children') === 'true') {
          e.preventDefault();
          const panelIndex = l2Item.getAttribute('data-l2-index');
          const groupsPanel = this.l2GroupsPanels.find(
            (panel) => panel.getAttribute('data-l2-panel') === panelIndex
          );
          if (groupsPanel) {
            const firstL3Link = groupsPanel.querySelector('.mega-menu__link--l3-group');
            if (firstL3Link) firstL3Link.focus();
          }
        }
      }
      
      // Moving back (L3 -> L2)
      if (e.key === 'ArrowLeft' && isL3GroupLink) {
        const groupsPanel = activeEl.closest('.mega-menu__l2-groups-panel');
        if (groupsPanel) {
          e.preventDefault();
          const panelIndex = groupsPanel.getAttribute('data-l2-panel');
          const l2Item = this.l2Items.find(
            (el) => el.getAttribute('data-l2-index') === panelIndex
          );
          if (l2Item) {
            const l2Link = l2Item.querySelector('.mega-menu__link--l2');
            if (l2Link) l2Link.focus();
          }
        }
      }
      
      // Moving deeper (L3 -> L3-CMS) if applicable
      if (e.key === 'ArrowRight' && isL3GroupLink && this.hasL3Cms) {
        const l3GroupItem = activeEl.closest('.mega-menu__item--l3-group');
        const cmsPanel = this.getCmsPanelForGroup(l3GroupItem);
        if (cmsPanel && cmsPanel.classList.contains('is-active')) {
          e.preventDefault();
          const firstCmsLink = cmsPanel.querySelector('.mega-menu__link--l3-group');
          if (firstCmsLink) firstCmsLink.focus();
        }
      }
      
      // Moving back (L3-CMS -> L3)
      if (e.key === 'ArrowLeft' && isL3GroupLink && activeEl.closest('.mega-menu__l3-cms-panel')) {
        if (this.activeL3Group) {
          e.preventDefault();
          const l3Link = this.activeL3Group.querySelector('.mega-menu__link--l3-group');
          if (l3Link) l3Link.focus();
        }
      }
    }

    cancelScheduledActivateL2(item) {
      if (item && this.pendingL2Item !== item) {
        return;
      }

      this.pendingL2Item = null;
      if (this.l2HoverTimer) {
        clearTimeout(this.l2HoverTimer);
        this.l2HoverTimer = null;
      }
    }

    scheduleActivateL2(item, l2Col) {
      if (!item || item === this.activeL2) {
        return;
      }

      this.cancelScheduledActivateL2();
      this.pendingL2Item = item;

      this.l2HoverTimer = setTimeout(() => {
        this.l2HoverTimer = null;

        if (this.pendingL2Item !== item) {
          return;
        }

        // Ignore pass-through hovers while browsing columns to the right.
        if (l2Col && !l2Col.matches(':hover')) {
          this.pendingL2Item = null;
          return;
        }

        this.pendingL2Item = null;
        this.activateL2(item);
      }, 160);
    }

    getGroupsPanelForL2(l2Item) {
      const panelIndex = l2Item?.getAttribute('data-l2-index');
      return this.l2GroupsPanels.find((panel) => panel.getAttribute('data-l2-panel') === panelIndex);
    }

    getCmsPanelForGroup(l3GroupItem) {
      if (!this.hasL3Cms || !l3GroupItem) return null;

      const groupLabel = normalizeLabel(l3GroupItem.getAttribute('data-menu-label'));
      if (!groupLabel) return null;

      return this.l3CmsPanels.find(
        (panel) => normalizeLabel(panel.getAttribute('data-l3-group-label')) === groupLabel
      );
    }

    findL3GroupInPanel(groupsPanel, groupLabel) {
      if (!groupsPanel || !groupLabel) return null;

      return (
        Array.from(groupsPanel.querySelectorAll(SELECTORS.l3GroupItem)).find(
          (el) => normalizeLabel(el.getAttribute('data-menu-label')) === groupLabel
        ) || null
      );
    }

    resolveBranchForGroupLabel(groupLabel) {
      if (!groupLabel) {
        return { l2Item: null, groupsPanel: null, l3Group: null };
      }

      const activeGroupsPanel = this.getGroupsPanelForL2(this.activeL2);
      const l3GroupInActiveL2 = this.findL3GroupInPanel(activeGroupsPanel, groupLabel);
      if (l3GroupInActiveL2 && this.activeL2) {
        return {
          l2Item: this.activeL2,
          groupsPanel: activeGroupsPanel,
          l3Group: l3GroupInActiveL2,
        };
      }

      if (this.activeL3Group) {
        const activeGroupLabel = normalizeLabel(this.activeL3Group.getAttribute('data-menu-label'));
        if (activeGroupLabel === groupLabel) {
          const groupsPanel = this.activeL3Group.closest(SELECTORS.l2GroupsPanel);
          const l2Item = this.l2Items.find(
            (el) => el.getAttribute('data-l2-index') === groupsPanel?.getAttribute('data-l2-panel')
          );
          if (l2Item && groupsPanel) {
            return { l2Item, groupsPanel, l3Group: this.activeL3Group };
          }
        }
      }

      for (const groupsPanel of this.l2GroupsPanels) {
        const l3Group = this.findL3GroupInPanel(groupsPanel, groupLabel);
        if (!l3Group) continue;

        const l2Item = this.l2Items.find(
          (el) => el.getAttribute('data-l2-index') === groupsPanel.getAttribute('data-l2-panel')
        );
        if (l2Item) {
          return { l2Item, groupsPanel, l3Group };
        }
      }

      return { l2Item: null, groupsPanel: null, l3Group: null };
    }

    applyBranch(branch) {
      if (!branch?.l2Item || !branch.groupsPanel || !branch.l3Group) {
        return;
      }

      this.activeL2 = branch.l2Item;
      this.l2Items.forEach((el) => el.classList.toggle(ACTIVE_CLASS, el === branch.l2Item));
      this.l2GroupsPanels.forEach((el) => el.classList.toggle(ACTIVE_CLASS, el === branch.groupsPanel));
      this.activeL3Group = branch.l3Group;
      branch.groupsPanel.querySelectorAll(SELECTORS.l3GroupItem).forEach((el) => {
        el.classList.toggle(ACTIVE_CLASS, el === branch.l3Group);
      });
    }

    activateL2(item) {
      if (!item) return;

      this.cancelScheduledActivateL2();

      const groupsPanel = this.getGroupsPanelForL2(item);
      const isSameL2 = this.activeL2 === item;

      this.activeL2 = item;
      this.l2Items.forEach((el) => {
        const isActive = el === item;
        el.classList.toggle(ACTIVE_CLASS, isActive);
        
        const link = el.querySelector('.mega-menu__link--l2');
        if (link) {
          link.setAttribute('aria-expanded', isActive ? 'true' : 'false');
          if (isActive) {
            link.setAttribute('aria-current', 'page');
          } else {
            link.removeAttribute('aria-current');
          }
        }
      });
      this.l2GroupsPanels.forEach((el) => el.classList.toggle(ACTIVE_CLASS, el === groupsPanel));

      const hasChildren = item.getAttribute('data-has-children') === 'true';
      if (!hasChildren) {
        this.setActiveL3Group(null, groupsPanel);
      } else if (!isSameL2) {
        this.setActiveL3Group(groupsPanel?.querySelector(SELECTORS.l3GroupItem) || null, groupsPanel);
      }

      this.syncState(true);
    }

    setActiveL3Group(item, groupsPanel) {
      this.activeL3Group = item;

      if (groupsPanel) {
        groupsPanel.querySelectorAll(SELECTORS.l3GroupItem).forEach((el) => {
          el.classList.toggle(ACTIVE_CLASS, el === item);
        });
      }

      const cmsPanel = this.getCmsPanelForGroup(item);
      this.l3CmsPanels.forEach((panel) => {
        panel.classList.toggle(ACTIVE_CLASS, panel === cmsPanel);
      });

      if (cmsPanel) {
        const firstCmsItem = cmsPanel.querySelector(SELECTORS.l3CmsItem);
        this.activeL3Cms = firstCmsItem;
        cmsPanel.querySelectorAll(SELECTORS.l3CmsItem).forEach((el) => {
          el.classList.toggle(ACTIVE_CLASS, el === firstCmsItem);
        });
      } else {
        this.activeL3Cms = null;
        this.l3CmsPanels.forEach((panel) => panel.classList.remove(ACTIVE_CLASS));
      }
    }

    activateL3Group(item) {
      if (!item) return;

      this.cancelScheduledActivateL2();

      const groupsPanel = item.closest(SELECTORS.l2GroupsPanel);
      if (groupsPanel) {
        const panelIndex = groupsPanel.getAttribute('data-l2-panel');
        const l2Item = this.l2Items.find((el) => el.getAttribute('data-l2-index') === panelIndex);
        if (l2Item) {
          this.activeL2 = l2Item;
          this.l2Items.forEach((el) => el.classList.toggle(ACTIVE_CLASS, el === l2Item));
          this.l2GroupsPanels.forEach((el) => el.classList.toggle(ACTIVE_CLASS, el === groupsPanel));
        }
      }

      this.setActiveL3Group(item, groupsPanel);
      this.syncState(true);
    }

    activateL3Cms(item) {
      if (!item) return;

      this.cancelScheduledActivateL2();

      const cmsPanel = item.closest(SELECTORS.l3CmsPanel);
      const groupLabel = normalizeLabel(cmsPanel?.getAttribute('data-l3-group-label'));
      const branch = this.resolveBranchForGroupLabel(groupLabel);

      this.applyBranch(branch);

      this.l3CmsPanels.forEach((panel) => {
        panel.classList.toggle(ACTIVE_CLASS, panel === cmsPanel);
      });

      this.activeL3Cms = item;
      cmsPanel?.querySelectorAll(SELECTORS.l3CmsItem).forEach((el) => {
        el.classList.toggle(ACTIVE_CLASS, el === item);
      });

      this.syncState(true);
    }

    updateMediaPanel(l3CmsLabel, l3GroupLabel, l2Label) {
      let nextPanel = null;
      const l2IsLeaf = this.activeL2?.getAttribute('data-has-children') !== 'true';
      const labelPriority = l2IsLeaf
        ? [l2Label]
        : [l3CmsLabel, l3GroupLabel, l2Label];

      for (const label of labelPriority.filter(Boolean)) {
        const match = this.mediaPanels.find(
          (panel) => normalizeLabel(panel.getAttribute('data-menu-label')) === label
        );
        if (match) {
          nextPanel = match;
          break;
        }
      }

      if (nextPanel === this.activeMediaPanel) {
        return;
      }

      this.activeMediaPanel = nextPanel;
      this.mediaPanels.forEach((panel) => {
        panel.classList.toggle(ACTIVE_CLASS, panel === nextPanel);
      });
    }

    updateColumnVisibility() {
      const hasChildren = this.activeL2?.getAttribute('data-has-children') === 'true';
      this.root.classList.toggle('mega-menu--l2-leaf-active', !hasChildren);

      const cmsPanel = hasChildren ? this.getCmsPanelForGroup(this.activeL3Group) : null;
      const hasCms = Boolean(cmsPanel);
      this.root.classList.toggle('mega-menu--l3-cms-active', hasCms);

      const hasMedia = this.mediaPanels.some((panel) => panel.classList.contains(ACTIVE_CLASS));
      this.root.classList.toggle('mega-menu--media-active', hasMedia);
    }

    syncState(shouldResize) {
      const l3CmsLabel = this.activeL3Cms
        ? normalizeLabel(this.activeL3Cms.getAttribute('data-menu-label'))
        : '';
      const l3GroupLabel = this.activeL3Group
        ? normalizeLabel(this.activeL3Group.getAttribute('data-menu-label'))
        : '';
      const l2Label = this.activeL2 ? normalizeLabel(this.activeL2.getAttribute('data-menu-label')) : '';

      this.updateMediaPanel(l3CmsLabel, l3GroupLabel, l2Label);
      this.updateColumnVisibility();

      if (shouldResize && !isDesignMode()) {
        syncDropdownHeight(this.root);
      }
    }
  }

  function initMegaMenus(scope) {
    (scope || document).querySelectorAll(SELECTORS.megaMenu).forEach((el) => {
      if (instances.has(el)) return;
      instances.set(el, new MegaMenu(el));
    });
  }

  function isDesktopNav() {
    return window.innerWidth >= (window.theme?.sizes?.small || 768);
  }

  function bindMegaMenuTopLinkClick(header) {
    if (!header || header.dataset.megaMenuTopLinkClick) return;

    header.dataset.megaMenuTopLinkClick = 'true';

    header.querySelectorAll('.menu__item.mega-menu-enabled [data-mega-menu-top-link]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (!isDesktopNav() || isDesignMode()) return;
        event.preventDefault();
      });
    });
  }

  function bindMegaMenuHoverGuard(header) {
    if (!header || header.dataset.megaMenuHoverGuard) return;

    header.dataset.megaMenuHoverGuard = 'true';

    header.querySelectorAll('.menu__item.mega-menu-enabled').forEach((navItem) => {
      const topLink = navItem.querySelector('.navlink--toplevel');
      if (!topLink) return;

      navItem.addEventListener(
        'mouseenter',
        (event) => {
          if (!isDesktopNav()) return;
          if (navItem.classList.contains(VISIBLE_CLASS)) return;

          if (topLink.matches(':hover')) return;

          event.stopImmediatePropagation();

          navItem.classList.remove(VISIBLE_CLASS);
          navItem.setAttribute('aria-expanded', 'false');

          const siteHeader = navItem.closest(SELECTORS.header);
          if (siteHeader && !siteHeader.querySelector(`.menu__item.mega-menu-enabled.${VISIBLE_CLASS}`)) {
            siteHeader.classList.remove(HEADER_MENU_OPENED_CLASS);
            document.body.classList.remove('megamenu-opened');
            resetHeaderBackground();
            resetDisclosureHeights();
          }
        },
        true
      );
    });
  }

  function bootMegaMenu(scope) {
    const header =
      scope?.querySelector?.(SELECTORS.header) ||
      (scope?.matches?.(SELECTORS.header) ? scope : null) ||
      document.querySelector(SELECTORS.header);

    initMegaMenus(header || document);
    bindHeaderBackgroundReset(header);
    bindMegaMenuHoverGuard(header);
    bindMegaMenuTopLinkClick(header);

    // Fix for Shift+Tab backwards navigation trapping focus in closed dropdowns
    if (header && !header.dataset.megaMenuShiftTabGuard) {
      header.dataset.megaMenuShiftTabGuard = 'true';
      header.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && e.shiftKey) {
          const activeEl = document.activeElement;
          if (activeEl && activeEl.classList.contains('navlink--toplevel')) {
            const allTopLinks = Array.from(header.querySelectorAll('.navlink--toplevel')).filter(
              link => link.offsetParent !== null
            );
            const currentIndex = allTopLinks.indexOf(activeEl);
            if (currentIndex > 0) {
              const prevLink = allTopLinks[currentIndex - 1];
              const prevItem = prevLink.closest('.menu__item');
              // If the previous item has a dropdown and is currently closed, bypass its hidden links
              if (prevItem && prevItem.querySelector('.header__dropdown') && !prevItem.classList.contains('is-visible')) {
                e.preventDefault();
                prevLink.focus();
              }
            }
          }
        }
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => bootMegaMenu());
  document.addEventListener('shopify:section:load', (event) => {
    bootMegaMenu(event.target);
  });
})();
